import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AuditStatus, dateTime, PaginationAPI } from '@shared';
import { examSessionsApi, PayloadExamSessions } from '../../../App';

export type SessionUI = {
  uuid: string;
  auditUuid: string;
  userFirstName: examSessionsApi.ExamSessionAPI['userFirstName'];
  userLastName: examSessionsApi.ExamSessionAPI['userLastName'];
  userEmail: string;
  examStartTime: string;
  examEndTime: string;
  examDuration: examSessionsApi.ExamSessionAPI['examDuration'];
  shortExam: examSessionsApi.ExamSessionAPI['shortExam'];
  recordOnlyExam: examSessionsApi.ExamSessionAPI['recordOnlyExam'];
  anomalyCount: examSessionsApi.ExamSessionAPI['anomalyCount'] | 'N/A';
  auditStatus: AuditStatus;
  clickable: boolean;
  auditOfInterest: boolean;
  failed: boolean;
};

export type SessionsUI = {
  pagination: PaginationAPI;
  sessions: SessionUI[];
};

type Args = {
  params: PayloadExamSessions;
  shouldRunQuery: boolean;
  partner: string;
  course: string;
  exam: string;
};

export function useExamSessions({
  partner,
  course,
  exam,
  params,
  shouldRunQuery = true,
}: Args): UseQueryResult<SessionsUI> {
  const secureBrowserOnlyStatus: AuditStatus = 'Secure Browser Only';
  const { offset, field, direction, limit, auditOfInterest } = params;
  const page = offset + 1;

  return useQuery(
    [
      'all exam sessions',
      exam,
      page,
      field,
      direction,
      limit,
      auditOfInterest,
      params.anomalyTypes,
      params.auditStatuses,
    ],
    () => examSessionsApi.getPack(partner, course, exam, { ...params }),
    {
      enabled: shouldRunQuery,
      select: ({ pagination, data }) => ({
        pagination,
        sessions: data.map((session) => ({
          auditStatus: session.auditStatus as AuditStatus,
          uuid: session.examSessionHandle,
          auditUuid: session.auditUuid,
          userFirstName: session.userFirstName,
          userLastName: session.userLastName,
          userEmail: session.userEmail ?? '',
          examStartTime: dateTime(session.examStartTime, 'exam start time'),
          examEndTime: dateTime(session.examEndTime, 'exam end time'),
          examDuration: session.examDuration,
          shortExam: session.shortExam,
          recordOnlyExam: session.recordOnlyExam,
          anomalyCount:
            session.auditStatus === secureBrowserOnlyStatus ? 'N/A' : session.anomalyCount,
          clickable:
            (session.auditStatus !== 'Processing' ||
              (session.auditStatus === 'Processing' && session.failed)) &&
            session.auditStatus !== secureBrowserOnlyStatus,
          auditOfInterest: session.auditOfInterest,
          failed: session.failed,
        })),
      }),
    }
  );
}
